import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IAvailableProgramExecutionGroupQuery } from '../interfaces/available-program-execution-group.query.interface';
import { ICourseExecutionProgramExecutionQuery } from '../interfaces/course-execution-program-execution.query.interface';
import { ICourseRelevantExecutionQuery } from '../interfaces/course-relevant-execution.query.interface';
import { AvailableProgramExecutionGroup } from '../models/available-program-execution-group.model';
import { CourseExecutionProgramExecution } from '../models/course-execution-program-execution.model';
import { CourseRelevantExecutionCommand } from '../models/course-relevant-execution.command.model';
import { CourseRelevantExecution } from '../models/course-relevant-execution.model';

@Injectable()
export class CourseRelevantExecutionApiService extends EntityService<ICourseRelevantExecutionQuery, CourseRelevantExecutionCommand> {
    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) private environment: IEnvironment) {
        super(http, environment.apiUrl, 'course-structure');
    }

    getAll(programExecutionId: string, courseOfferingId: string, search?: string, context?: string): Observable<CourseRelevantExecution[]> {
        let params = new HttpParams();

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (context) {
            params = params.set('context', context);
        }
        return this.http
            .get<ICourseRelevantExecutionQuery[]>(
                `${this.apiUrl}/${this.entities}/program-executions/${programExecutionId}/course-offerings/${courseOfferingId}/relevant-executions`,
                { params }
            )
            .pipe(map((queries) => queries.map((query) => new CourseRelevantExecution(query))));
    }

    getProgramExecutions(courseExecutionId: string, search?: string): Observable<CourseExecutionProgramExecution[]> {
        let params = new HttpParams();

        if (search) {
            params = params.set('filter[query]', search);
        }

        return this.http
            .get<ICourseExecutionProgramExecutionQuery[] | { data: ICourseExecutionProgramExecutionQuery[] }>(
                `${this.apiUrl}/${this.entities}/course-executions/${courseExecutionId}/program-executions`,
                { params }
            )
            .pipe(
                map((response) => {
                    const queries = Array.isArray(response) ? response : response?.data || [];

                    return queries.map((query) => new CourseExecutionProgramExecution(query));
                })
            );
    }

    getAvailableProgramExecutions(courseExecutionId: string, search?: string): Observable<AvailableProgramExecutionGroup[]> {
        let params = new HttpParams();

        if (search) {
            params = params.set('filter[query]', search);
        }

        return this.http
            .get<IAvailableProgramExecutionGroupQuery[] | { data: IAvailableProgramExecutionGroupQuery[] }>(
                `${this.apiUrl}/${this.entities}/course-executions/${courseExecutionId}/available-program-executions`,
                {
                    params,
                }
            )
            .pipe(
                map((response) => {
                    const queries = Array.isArray(response) ? response : response?.data || [];

                    return queries
                        .filter((query) => !!query.program_offering && !!query.executions?.length)
                        .map((query) => new AvailableProgramExecutionGroup(query));
                })
            );
    }

    create(entity: CourseRelevantExecution): Observable<CourseRelevantExecution> {
        const data = new CourseRelevantExecutionCommand(entity);

        return this._create(data).pipe(map((query) => new CourseRelevantExecution(query)));
    }

    read(id: string): Observable<CourseRelevantExecution> {
        return this._read(id).pipe(map((query) => new CourseRelevantExecution(query)));
    }

    update(programExecutionId: string, courseExecutionId: string, isDefault: boolean): Observable<CourseRelevantExecution> {
        const data = {
            is_default: isDefault,
        };

        return this.http
            .patch<ICourseRelevantExecutionQuery>(
                `${this.apiUrl}/${this.entities}/program-executions/${programExecutionId}/relevant-executions/${courseExecutionId}/is-default`,
                data
            )
            .pipe(map((query) => new CourseRelevantExecution(query)));
    }

    createRelevantExecutions(programExecutionId: string, courseOfferingId: string, courseExecutionIds: string[]): Observable<CourseRelevantExecution[]> {
        const data = {
            program_execution_id: Number(programExecutionId),
            course_offering_id: Number(courseOfferingId),
            course_execution_ids: courseExecutionIds.map((id) => Number(id)),
        };

        return this.http
            .post<ICourseRelevantExecutionQuery[]>(`${this.apiUrl}/${this.entities}/relevant-executions`, data)
            .pipe(map((queries) => queries.map((query) => new CourseRelevantExecution(query))));
    }

    createRelevantExecutionsForCourseExecution(
        courseOfferingId: string,
        courseExecutionId: string,
        programExecutionIds: string[],
        isDefault: boolean
    ): Observable<CourseRelevantExecution[]> {
        const data = {
            course_offering_id: Number(courseOfferingId),
            course_execution_ids: [Number(courseExecutionId)],
            program_execution_ids: programExecutionIds.map((id) => Number(id)),
            is_default: isDefault,
        };

        return this.http
            .post<ICourseRelevantExecutionQuery[]>(`${this.apiUrl}/${this.entities}/relevant-executions`, data)
            .pipe(map((queries) => queries.map((query) => new CourseRelevantExecution(query))));
    }

    delete(programExecutionId: string, courseOfferingId: string, courseExecutionIds: string[]): Observable<void> {
        const options = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            body: {
                program_execution_id: Number(programExecutionId),
                course_offering_id: Number(courseOfferingId),
                course_execution_ids: courseExecutionIds.map((id) => Number(id)),
            },
        };

        return this.http.delete<void>(`${this.apiUrl}/${this.entities}/relevant-executions`, options);
    }
}
